import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { ITEM_TABLE_TYPE, SCALING_TABLE_TYPE, SEEQ_DATASOURCE } from '@/main/app.constants';
import { HierarchyNodeType, TABLE_DEFINITION_SUBTYPES } from '@/search/search.constants';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { Datasource, DecoratedItemPreviewV1, SelectedSearchItem } from '@/search/search.types';
import type { HierarchyNodeOutputV1 } from '@seeqdev/api/model/HierarchyNodeOutputV1';

/*
 * Limit of items shown in the overview mode of the Search Widget
 */
export const LIMIT_IN_OVERVIEW = 8;

type ShowOnlyTheseDatasourcesOptions = {
  includeNewDatasources?: boolean;
};

/**
 * Filter datasources based on user datasource preferences while always including the Seeq datasource and
 * datasources scoped to the current workbook.
 */
export function showOnlyTheseDatasources(
  workbookId: string,
  datasources: Datasource[],
  userDatasources: string[],
  { includeNewDatasources }: ShowOnlyTheseDatasourcesOptions = { includeNewDatasources: false },
): Datasource[] {
  return datasources.filter((datasource) => {
    if (
      datasource.datasourceClass === SEEQ_DATASOURCE.datasourceClass ||
      (workbookId && datasource.scopedTo === workbookId)
    ) {
      return true;
    } else if (includeNewDatasources) {
      return !userDatasources.includes(datasource.id);
    } else {
      return userDatasources.includes(datasource.id);
    }
  });
}

/**
 * Checks if search types are extracted, as these types can't be mixed with other item types. TableDefinition
 * Types have a frontend Pseudo-type to check as well.
 * An invalid result is defined as combining {@link SCALING_TABLE_TYPE} or {@link ITEM_TABLE_TYPE} or
 * {@link SeeqNames.Types.Hierarchy} with other search types.
 */
export const hasInvalidSearchTypes = (types: string[]) => {
  const onlyTableTypes = types.every((type) => TABLE_DEFINITION_SUBTYPES.includes(type));
  const onlyHierarchyType = types.every((type) => type === SeeqNames.Types.Hierarchy);
  if (onlyTableTypes || onlyHierarchyType) {
    return false;
  }

  return (
    types.length > 1 &&
    types.some((type) => TABLE_DEFINITION_SUBTYPES.includes(type) || type === SeeqNames.Types.Hierarchy)
  );
};

export const filterOutInvalidSearchTypes = (types: string[] | undefined): string[] | undefined => {
  if (!types || !hasInvalidSearchTypes(types)) {
    return types;
  }
  return types.filter((type) => !TABLE_DEFINITION_SUBTYPES.includes(type) && type !== SeeqNames.Types.Hierarchy);
};

export const replacePseudoTypesWithBackendType = (searchTypes: string[] | string): string | string[] => {
  if (!searchTypes) {
    return searchTypes;
  }
  if (!_.isArray(searchTypes)) {
    return TABLE_DEFINITION_SUBTYPES.includes(searchTypes) ? SeeqNames.Types.TableDefinition : searchTypes;
  }
  return _.chain(searchTypes)
    .map((type) => (TABLE_DEFINITION_SUBTYPES.includes(type) ? SeeqNames.Types.TableDefinition : type))
    .uniqBy((type) => type)
    .value();
};

const SCALING_CSV_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.SCALINGCSV}`;
const SCALING_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.SCALING}`;
const ITEM_CSV_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.ITEMCSV}`;
const ITEM_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.ITEM}`;
type TableTypeToFilter = 'Scaling' | 'Item' | 'Both';

const transformSingleFilterToIncludeTableType = (filter: string, type: TableTypeToFilter): string[] => {
  if (type === 'Both') {
    return [
      filter.concat(SCALING_FILTER),
      filter.concat(SCALING_CSV_FILTER),
      filter.concat(ITEM_FILTER),
      filter.concat(ITEM_CSV_FILTER),
    ];
  }
  if (type === 'Scaling') {
    return [filter.concat(SCALING_FILTER), filter.concat(SCALING_CSV_FILTER)];
  } else {
    return [filter.concat(ITEM_FILTER), filter.concat(ITEM_CSV_FILTER)];
  }
};

export const updateTypesAndFiltersForTableDefinitions = (
  searchTypes: string[] | string,
  currentFilters: string[] | string,
): { updatedTypes: string[] | string; updatedFilters: string[] | string } => {
  let type: TableTypeToFilter = 'Both';
  if (_.includes(searchTypes, SCALING_TABLE_TYPE) && !_.includes(searchTypes, ITEM_TABLE_TYPE)) {
    type = 'Scaling';
  } else if (!_.includes(searchTypes, SCALING_TABLE_TYPE) && _.includes(searchTypes, ITEM_TABLE_TYPE)) {
    type = 'Item';
  }
  let updatedFilters = addFilterForTableDefinitionSubTypes(searchTypes, currentFilters, type);
  let updatedTypes = replacePseudoTypesWithBackendType(searchTypes);
  return { updatedTypes, updatedFilters };
};

export const addFilterForTableDefinitionSubTypes = (
  searchTypes: string[] | string,
  currentFilters: string[] | string,
  type: TableTypeToFilter,
): string | string[] => {
  if (
    !_.includes(searchTypes, SeeqNames.Types.TableDefinition) &&
    !_.includes(searchTypes, SCALING_TABLE_TYPE) &&
    !_.includes(searchTypes, ITEM_TABLE_TYPE)
  ) {
    return currentFilters;
  }
  if (typeof currentFilters === 'string') {
    if (currentFilters.includes(SeeqNames.API.Flags.ExcludeGloballyScoped)) {
      return [currentFilters].concat(transformSingleFilterToIncludeTableType('', type));
    }
    return transformSingleFilterToIncludeTableType(currentFilters, type);
  }

  const [potentialExcludeGloballyScoped, filters] = _.partition(
    currentFilters,
    (filter) => filter === SeeqNames.API.Flags.ExcludeGloballyScoped,
  );
  // If the search type is TableDefinition, but filters are empty, we still need to add the filters.
  const filtersToProcess = filters.length === 0 ? [''] : filters;
  return potentialExcludeGloballyScoped.concat(
    filtersToProcess.flatMap((filter) => transformSingleFilterToIncludeTableType(filter, type)),
  );
};

export const nodeToSelectedSearchItem = (
  node: HierarchyNodeOutputV1,
  ancestors: SelectedSearchItem[],
): SelectedSearchItem => {
  const buildFallbackId = (currentNode: HierarchyNodeOutputV1, ancestorItems: SelectedSearchItem[]): string => {
    const lastAncestorId = ancestorItems.length > 0 ? ancestorItems[ancestorItems.length - 1].id : undefined;
    const currentPart = `${currentNode.displayName}-${currentNode.columnId}`;
    return lastAncestorId ? `${lastAncestorId}|${currentPart}` : currentPart;
  };

  const id = node.itemMetadata ? node.itemMetadata.id : buildFallbackId(node, ancestors);
  return {
    id,
    node,
    name: node.displayName,
    type: HierarchyNodeType,
    ancestors: ancestors.map(toAncestorItem),
    hasActiveChildren: !node.leaf,
  } as SelectedSearchItem;
};

/**
 * Converts a hierarchy node and its path to a SelectedSearchItem, including all ancestors.
 * @param node - current node
 * @param nodePath = path from root to the current node
 * @param hierarchyOutput - hierarchy this node path lives in
 */
export const nodeAndPathToSelectedSearchItem = (
  node: HierarchyNodeOutputV1,
  nodePath: HierarchyNodeOutputV1[],
  hierarchyOutput: SelectedSearchItem,
): SelectedSearchItem => {
  const nodePathAsAncestors: SelectedSearchItem[] = [hierarchyOutput];
  for (let i = 0; i < nodePath.length; i++) {
    const pathNode = nodePath[i];
    const thisAncestorAsSelectedSearchItem = nodeToSelectedSearchItem(pathNode, _.cloneDeep(nodePathAsAncestors));
    nodePathAsAncestors.push(thisAncestorAsSelectedSearchItem);
  }
  return nodeToSelectedSearchItem(node, nodePathAsAncestors);
};

/**
 * Fetches the children of a hierarchy node, and return them as SelectedSearchItems with ancestors.
 * @param hierarchyId - id of the hierarchy
 * @param currentItemWithNode - current item with node and ancestors
 * @param currentNodePath - path from root to the current node
 */
export const isScalingTable = (
  item: SelectedSearchItem & { includedProperties?: Record<string, { value?: string }> },
): boolean => {
  return item.includedProperties?.[SeeqNames.Properties.TableType]?.value?.includes('SCALING') ?? false;
};

export const isItemTable = (
  item: SelectedSearchItem & { includedProperties?: Record<string, { value?: string }> },
): boolean => {
  return item.includedProperties?.[SeeqNames.Properties.TableType]?.value?.includes('ITEM') ?? false;
};

export const toAncestorItem = (
  item: Pick<SelectedSearchItem, 'id' | 'name' | 'type' | 'datumId' | 'isScalingTableCSVFlow' | 'node'>,
): DecoratedItemPreviewV1 => {
  const result: DecoratedItemPreviewV1 = {
    id: item.id,
    name: item.name ?? '',
    type: item.type ?? '',
  };
  if (item.datumId !== undefined) result.datumId = item.datumId;
  if (item.isScalingTableCSVFlow !== undefined) result.isScalingTableCSVFlow = item.isScalingTableCSVFlow;
  if (item.node !== undefined) result.node = item.node;
  return result;
};
